import React, { useState, useEffect, useRef } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { Note, Scale, Interval, Chord } from 'tonal';
import { degToRad } from 'three/src/math/MathUtils.js';
import { convertToSharp } from '../utils/noteHelpers';
import { NoteBase } from './NoteBase';
import { getThreeJsColor } from '../utils/daisyColors';
import { Text } from '@react-three/drei';
import { Color, Group } from 'three';

// Interface for FretboardSceneProps
interface FretboardSceneProps {
  tuning: string[];
  selectedKey: string;
  selectedScale: string;
  selectedChord: string;
  numberOfFrets: number;
  stringSpacing?: number;
  fretSpacing?: number;
  isLeftHanded?: boolean;
  hasDroneString?: boolean;
  hideDroneStringNotes?: boolean;
  droneStringStartingFret?: number;
  rotation?: number;
  showFretNumbers: boolean;
  capoFret: number;
  droneCapoFret: number;
  useCapo: boolean;
  noteViewMode: 'letters' | 'scaleDegrees';
  isChordDetectionMode?: boolean;
  selectedNotes?: string[];
  setSelectedNotes?: (notes: string[]) => void;
  showNoteOctaves?: boolean;
}

// Main component rendering the fretboard scene
const FretboardScene: React.FC<FretboardSceneProps> = (props) => {
  const {
    stringSpacing = 1.5,
    fretSpacing = 2,
    isLeftHanded = false,
    hasDroneString = true,
    hideDroneStringNotes: hideFrettedDroneStringNotes = true,
    droneStringStartingFret = 5,
    rotation = 90
  } = props;

  const [fretboardColor, setFretboardColor] = useState(getThreeJsColor('light', 'base-100'));
  const [fretColor, setFretColor] = useState(getThreeJsColor('light', 'base-content'));
  const [stringColor, setStringColor] = useState(getThreeJsColor('light', 'base-content'));
  const [disabledNoteColor, setDisabledNoteColor] = useState(getThreeJsColor('light', 'base-200'));
  const [noteOutlineColor, setNoteOutlineColor] = useState(getThreeJsColor('light', 'base-200'));

  useEffect(() => {
    const updateColors = (event) => {
      setFretboardColor(getThreeJsColor(event.detail.theme, 'base-100'));
      setFretColor(getThreeJsColor(event.detail.theme, 'base-content'));
      setStringColor(getThreeJsColor(event.detail.theme, 'base-content'));
      setDisabledNoteColor(getThreeJsColor(event.detail.theme, 'base-300'));
      setNoteOutlineColor(getThreeJsColor(event.detail.theme, 'base-content'));
    };

    document.addEventListener('daisyui-theme-change', updateColors);

    return () => {
      document.removeEventListener('daisyui-theme-change', updateColors);
    };
  }, []);

  return (
    <div style={{ width: '100%', height: '100%'}}>
      <Canvas
        orthographic
        camera={{ zoom: 20, position: [0, 0, 100], near: 1, far: 1000 }}
        style={{ width: '100%', height: '100%'}}
      >
        <FretboardSizer
          {...props}
          stringSpacing={stringSpacing}
          fretSpacing={fretSpacing}
          isLeftHanded={isLeftHanded}
          hasDroneString={hasDroneString}
          hideDroneStringNotes={hideFrettedDroneStringNotes}
          droneStringStartingFret={droneStringStartingFret}
          rotation={rotation}
          fretboardColor={fretboardColor}
          fretColor={fretColor}
          stringColor={stringColor}
          disabledNoteColor={disabledNoteColor}
          noteOutlineColor={noteOutlineColor}
        />
      </Canvas>
    </div>
  );
};

// Interface for FretboardSizerProps, extending FretboardSceneProps
interface FretboardSizerProps extends FretboardSceneProps { 
  fretboardColor: Color;
  fretColor: Color;
  stringColor: Color;
  disabledNoteColor: Color;
  noteOutlineColor: Color;
}

// Component to handle sizing and scaling of the fretboard
const FretboardSizer: React.FC<FretboardSizerProps> = (props) => {
  const { viewport, size } = useThree();
  const ref = useRef<Group>(null);

  const width = props.tuning.length * props.stringSpacing!;
  const height = props.numberOfFrets * props.fretSpacing!;
  const rotationInRadians = degToRad(props.rotation ?? 0);

  useEffect(() => {
    const handleResize = () => {
      if (ref.current) {
        const cosRotation = Math.abs(Math.cos(rotationInRadians));
        const sinRotation = Math.abs(Math.sin(rotationInRadians));
        const boundingWidth = width * cosRotation + height * sinRotation;
        const boundingHeight = width * sinRotation + height * cosRotation;

        const scaleWidth = size.width / boundingWidth;
        const scaleHeight = size.height / boundingHeight;
        const scale = Math.min(scaleWidth, scaleHeight) * 0.80 + .08;

        ref.current.scale.set(scale, scale, 1);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Initial sizing

    return () => window.removeEventListener('resize', handleResize);
  }, [size, width, height, rotationInRadians]);

  useFrame(() => {
    if (ref.current) {
      const cosRotation = Math.abs(Math.cos(rotationInRadians));
      const sinRotation = Math.abs(Math.sin(rotationInRadians));
      const boundingWidth = width * cosRotation + height * sinRotation;
      const boundingHeight = width * sinRotation + height * cosRotation;

      const scaleWidth = viewport.width / boundingWidth;
      const scaleHeight = viewport.height / boundingHeight;
      const scale = Math.min(scaleWidth, scaleHeight) * 0.80 + .08;

      ref.current.scale.set(scale, scale, 1);
    }
  });

  return (
    <group ref={ref} rotation={[0, 0, rotationInRadians]}>
      <Fretboard {...props} width={width} height={height} rotation={rotationInRadians} />
    </group>
  );
};

// Interface for FretboardProps, extending FretboardSceneProps
interface FretboardProps extends FretboardSceneProps {
  rotation: number;
  width: number;
  height: number;
  fretboardColor: Color;
  fretColor: Color;
  stringColor: Color;
  disabledNoteColor: Color;
  noteOutlineColor: Color;
}

// Helper function to get ordered tuning
const getOrderedTuning = (tuning: string[], isLeftHanded: boolean, hasDroneString: boolean): string[] => {
  let orderedTuning = isLeftHanded ? [...tuning].reverse() : tuning;
  if (isLeftHanded && hasDroneString) {
    orderedTuning = orderedTuning.slice(0, -1);
    orderedTuning.push(tuning[0]); // add the drone string at the end
  }
  return orderedTuning;
};

// Component to render the fretboard
const Fretboard: React.FC<FretboardProps> = ({
  tuning,
  selectedKey,
  selectedScale: scale,
  selectedChord,
  numberOfFrets,
  rotation,
  width,
  height,
  stringSpacing = 1.5,
  fretSpacing = 2,
  isLeftHanded = false,
  hasDroneString = true,
  hideDroneStringNotes: hideFrettedDroneStringNotes = true,
  droneStringStartingFret = 5,
  fretColor,
  fretboardColor,
  stringColor,
  disabledNoteColor,
  noteOutlineColor,
  showFretNumbers,
  capoFret,
  droneCapoFret,
  useCapo,
  noteViewMode,
  isChordDetectionMode,
  selectedNotes,
  setSelectedNotes,
  showNoteOctaves,
}) => {
  const rawScaleNotes = Scale.get(`${selectedKey} ${scale}`).notes;
  const scaleNotes = rawScaleNotes.map(note =>
    scale === 'chromatic' ? convertToSharp(note) : Note.pitchClass(note)
  );

  const chordNotes = selectedChord ? Chord.get(selectedChord).notes.map(Note.pitchClass) : [];
  const scaleNoteMap = new Map(scaleNotes.map(note => [Note.chroma(note), note]));

  const orderedTuning = getOrderedTuning(tuning, isLeftHanded, hasDroneString);

  const noteOffset = fretSpacing / 2;

  return (
    <group position={[-width / 2 + stringSpacing / 2, height / 2 - noteOffset / 1.25, 0]}>
      <FretboardVisFlat
        width={width}
        height={height}
        fretSpacing={fretSpacing}
        numberOfFrets={numberOfFrets}
        stringSpacing={stringSpacing}
        hasDroneString={hasDroneString}
        droneStringStartingFret={droneStringStartingFret}
        isLeftHanded={isLeftHanded}
        fretboardColor={fretboardColor}
      />
      <Frets
        numberOfFrets={numberOfFrets}
        stringSpacing={stringSpacing}
        fretSpacing={fretSpacing}
        tuningLength={tuning.length}
        hasDroneString={hasDroneString}
        droneStringStartingFret={droneStringStartingFret}
        isLeftHanded={isLeftHanded}
        fretColor={fretColor}
        showFretNumbers={showFretNumbers}
        capoFret={capoFret}
        droneCapoFret={droneCapoFret}
        useCapo={useCapo}
        rotation={rotation}
      />
      <Strings
        tuning={orderedTuning}
        numberOfFrets={numberOfFrets}
        stringSpacing={stringSpacing}
        fretSpacing={fretSpacing}
        scaleNoteMap={scaleNoteMap}
        chordNotes={chordNotes}
        rotation={rotation}
        hasDroneString={hasDroneString}
        hideFrettedDroneStringNotes={hideFrettedDroneStringNotes}
        droneStringStartingFret={droneStringStartingFret}
        isLeftHanded={isLeftHanded}
        stringColor={stringColor}
        disabledNoteColor={disabledNoteColor}
        noteOutlineColor={noteOutlineColor}
        capoFret={capoFret}
        droneCapoFret={droneCapoFret}
        useCapo={useCapo}
        noteViewMode={noteViewMode}
        scaleNotes={scaleNotes}
        isChordDetectionMode={isChordDetectionMode}
        selectedNotes={selectedNotes}
        setSelectedNotes={setSelectedNotes}
        showNoteOctaves={showNoteOctaves}
      />
    </group>
  );
};

// Interface for FretsProps
interface FretsProps {
  numberOfFrets: number;
  stringSpacing: number;
  fretSpacing: number;
  tuningLength: number;
  hasDroneString: boolean;
  droneStringStartingFret: number;
  isLeftHanded: boolean;
  fretColor: Color;
  showFretNumbers: boolean;
  capoFret: number;
  droneCapoFret: number;
  useCapo: boolean;
  rotation: number;
}

// Component to render frets
const Frets: React.FC<FretsProps> = ({
  numberOfFrets,
  stringSpacing,
  fretSpacing,
  tuningLength,
  hasDroneString,
  droneStringStartingFret,
  isLeftHanded,
  fretColor,
  showFretNumbers,
  capoFret,
  droneCapoFret,
  useCapo,
  rotation,
}) => {
  return (
    <>
      {Array.from({ length: numberOfFrets + 1 }, (_, fretIndex) => (
        <React.Fragment key={`fret-${fretIndex}`}>
          <FretVisFlat
            fretIndex={fretIndex}
            stringSpacing={stringSpacing}
            fretSpacing={fretSpacing}
            tuningLength={tuningLength}
            hasDroneString={hasDroneString}
            droneStringStartingFret={droneStringStartingFret}
            isLeftHanded={isLeftHanded}
            fretColor={fretColor}
          />
          {showFretNumbers && fretIndex > 0 && (
            <FretNumber
              fretIndex={fretIndex}
              stringSpacing={stringSpacing}
              fretSpacing={fretSpacing}
              tuningLength={tuningLength}
              isLeftHanded={isLeftHanded}
              fretNumberColor={fretColor}
              rotation={rotation}
            />
          )}
        </React.Fragment>
      ))}
      {useCapo && capoFret > 0 && (
        <Capo
          fretIndex={capoFret}
          stringSpacing={stringSpacing}
          fretSpacing={fretSpacing}
          tuningLength={tuningLength}
          hasDroneString={hasDroneString}
          isLeftHanded={isLeftHanded}
          isDroneCapo={false}
          capoColor={fretColor}
        />
      )}
      {useCapo && hasDroneString && droneCapoFret > 0 && droneCapoFret <= numberOfFrets && (
        <Capo
          fretIndex={droneCapoFret}
          stringSpacing={stringSpacing}
          fretSpacing={fretSpacing}
          tuningLength={tuningLength}
          hasDroneString={hasDroneString}
          isLeftHanded={isLeftHanded}
          isDroneCapo={true}
          capoColor={fretColor}
        />
      )}
    </>
  );
};

// Interface for StringsProps
interface StringsProps {
  tuning: string[];
  numberOfFrets: number;
  stringSpacing: number;
  fretSpacing: number;
  scaleNoteMap: Map<number, string>;
  chordNotes: string[];
  rotation: number;
  hasDroneString: boolean;
  hideFrettedDroneStringNotes: boolean;
  droneStringStartingFret: number;
  isLeftHanded: boolean;
  stringColor: Color;
  disabledNoteColor: Color;
  noteOutlineColor: Color;
  capoFret: number;
  droneCapoFret: number;
  useCapo: boolean;
  noteViewMode: 'letters' | 'scaleDegrees';
  scaleNotes: string[];
  isChordDetectionMode?: boolean;
  selectedNotes?: string[];
  setSelectedNotes?: (notes: string[]) => void;
  showNoteOctaves?: boolean;
}

// Component to render strings
const Strings: React.FC<StringsProps> = ({
  tuning,
  numberOfFrets,
  stringSpacing,
  fretSpacing,
  scaleNoteMap,
  chordNotes,
  rotation,
  hasDroneString,
  hideFrettedDroneStringNotes,
  droneStringStartingFret,
  isLeftHanded,
  stringColor,
  disabledNoteColor,
  noteOutlineColor,
  capoFret,
  droneCapoFret,
  useCapo,
  noteViewMode,
  scaleNotes,
  isChordDetectionMode,
  selectedNotes,
  setSelectedNotes,
  showNoteOctaves,
}) => {
  return (
    <>
      {tuning.map((stringNote, stringIndex) => {
        const isDroneString = hasDroneString && ((isLeftHanded && stringIndex === tuning.length - 1) || (!isLeftHanded && stringIndex === 0));
        const effectiveCapoFret = useCapo ? (isDroneString ? droneCapoFret : capoFret) : 0;
        return (
          <String
            key={`string-${stringIndex}`}
            stringNote={stringNote}
            stringIndex={stringIndex}
            numberOfFrets={numberOfFrets}
            stringSpacing={stringSpacing}
            fretSpacing={fretSpacing}
            scaleNoteMap={scaleNoteMap}
            chordNotes={chordNotes}
            rotation={rotation}
            isDroneString={isDroneString}
            hideFrettedDroneStringNotes={hideFrettedDroneStringNotes && isDroneString}
            droneStringStartingFret={droneStringStartingFret}
            stringColor={stringColor}
            disabledNoteColor={disabledNoteColor}
            noteOutlineColor={noteOutlineColor}
            capoFret={effectiveCapoFret}
            useCapo={useCapo}
            noteViewMode={noteViewMode}
            scaleNotes={scaleNotes}
            isChordDetectionMode={isChordDetectionMode}
            selectedNotes={selectedNotes}
            setSelectedNotes={setSelectedNotes}
            showNoteOctaves={showNoteOctaves}
          />
        );
      })}
    </>
  );
};

// Interface for StringProps
interface StringProps {
  stringNote: string;
  stringIndex: number;
  numberOfFrets: number;
  stringSpacing: number;
  fretSpacing: number;
  scaleNoteMap: Map<number, string>;
  chordNotes: string[];
  rotation: number;
  isDroneString: boolean;
  hideFrettedDroneStringNotes: boolean;
  droneStringStartingFret: number;
  stringColor: Color;
  disabledNoteColor: Color;
  noteOutlineColor: Color;
  capoFret: number;
  useCapo: boolean;
  noteViewMode: 'letters' | 'scaleDegrees';
  scaleNotes: string[];
  isChordDetectionMode?: boolean;
  selectedNotes?: string[];
  setSelectedNotes?: (notes: string[]) => void;
  showNoteOctaves?: boolean;
}

// Component to render a single string
const String: React.FC<StringProps> = ({
  stringNote,
  stringIndex,
  numberOfFrets,
  stringSpacing,
  fretSpacing,
  scaleNoteMap,
  chordNotes,
  rotation,
  isDroneString,
  hideFrettedDroneStringNotes,
  droneStringStartingFret,
  stringColor,
  disabledNoteColor,
  noteOutlineColor,
  capoFret,
  useCapo,
  noteViewMode,
  scaleNotes,
  isChordDetectionMode,
  selectedNotes,
  setSelectedNotes,
  showNoteOctaves,
}) => {
  const stringPositionY = isDroneString ? -droneStringStartingFret * fretSpacing : 0;
  const stringVisFlatPositionY = isDroneString ? -numberOfFrets * fretSpacing / 2 + droneStringStartingFret : -numberOfFrets * fretSpacing / 2;

  const handleNoteClick = (note: string, fretIndex: number) => {
    if (!isChordDetectionMode || !setSelectedNotes || !selectedNotes) return;
    
    // Create a unique identifier for this note position and include the actual note
    const notePosition = `${stringIndex}-${fretIndex}-${note}`;
    
    // Check if this exact note is already selected
    if (selectedNotes.includes(notePosition)) {
      // If it is, remove it
      setSelectedNotes(selectedNotes.filter(n => n !== notePosition));
    } else {
      // If not, remove any existing note on this string and add the new note
      const filteredNotes = selectedNotes.filter(n => !n.startsWith(`${stringIndex}-`));
      setSelectedNotes([...filteredNotes, notePosition]);
    }
  };

  return (
    <group key={`string-${stringIndex}`} position={[stringIndex * stringSpacing, stringPositionY, 0]}>
      <StringVisFlat
        fretSpacing={fretSpacing}
        numberOfFrets={numberOfFrets}
        isDroneString={isDroneString}
        positionY={stringVisFlatPositionY}
        droneStringStartingFret={droneStringStartingFret}
        stringColor={stringColor}
      />
      {Array.from({ length: numberOfFrets + 1 }, (_, fretIndex) => {
        const shouldRenderNote = true;
        const effectiveCapoFret = useCapo ? capoFret : 0;

if (isDroneString) {
  // Don't show notes beyond the number of frets
  if (fretIndex + droneStringStartingFret > numberOfFrets) return null;

  if (effectiveCapoFret === 0) {
    // When no capo is used
    if (hideFrettedDroneStringNotes) {
      // Only show the open note and the top fretted note
      if (fretIndex !== 0 && fretIndex !== numberOfFrets - droneStringStartingFret+1) return null;
    }
  } else {
    // When capo is used
    // Only show notes from capo position onwards
    if (fretIndex + droneStringStartingFret < effectiveCapoFret) return null;

    if (hideFrettedDroneStringNotes) {
      // Only show the note at the capo position
      if (fretIndex + droneStringStartingFret !== effectiveCapoFret) return null;
    }
  }

  // Handle the case where the number of frets is less than the drone string starting fret
  if (fretIndex < effectiveCapoFret && numberOfFrets < droneStringStartingFret) return null;
} else {
  // For regular strings, don't show notes before the capo
  if (fretIndex < effectiveCapoFret) return null;
}

// If we've made it here, the note should be rendered
if (!shouldRenderNote) return null;
          

        const note = Note.transpose(stringNote, Interval.fromSemitones(fretIndex));
        const noteClass = Note.pitchClass(note);
        const chroma = Note.chroma(note);
        const isInScale = scaleNoteMap.has(chroma);
        // In chord detection mode, always use the actual note name
        const noteName = isChordDetectionMode ? noteClass : (isInScale ? scaleNoteMap.get(chroma) : noteClass);
        const isInChord = chordNotes.some(chordNote => Note.chroma(chordNote) === chroma);
        const isChordEnabled = chordNotes.length > 0;
        const notePosition = `${stringIndex}-${fretIndex}-${note}`;
        const isSelected = selectedNotes?.includes(notePosition) || false;
        const octave = Note.octave(note);

        // Determine the scale degree
        const scaleDegree = isInScale ? scaleNotes.indexOf(noteName) + 1 : null;

        return (
          <NoteBase
            key={fretIndex}
            noteName={noteName as string}
            position={[0, -fretIndex * fretSpacing + fretSpacing / 2, 0]}
            isInScale={isInScale}
            isChordEnabled={isChordEnabled}
            isInChord={isInChord}
            isFrettable={fretIndex === effectiveCapoFret || (isDroneString && hideFrettedDroneStringNotes)}
            rotation={rotation}
            disabledNoteColor={disabledNoteColor}
            noteOutlineColor={noteOutlineColor}
            noteViewMode={noteViewMode}
            scaleDegree={scaleDegree}
            isChordDetectionMode={isChordDetectionMode}
            isSelected={isSelected}
            onClick={() => handleNoteClick(note, fretIndex)}
            showOctave={showNoteOctaves}
            octave={octave}
          />
        );
      })}
    </group>
  );
};

// Interface for FretboardVisFlatProps
interface FretboardVisFlatProps {
  width: number;
  height: number;
  fretSpacing: number;
  numberOfFrets: number;
  stringSpacing: number;
  hasDroneString: boolean;
  droneStringStartingFret: number;
  isLeftHanded: boolean;
  fretboardColor: Color;
}

// Component to visualize the flat fretboard
const FretboardVisFlat: React.FC<FretboardVisFlatProps> = ({
  width,
  height,
  fretSpacing,
  numberOfFrets,
  stringSpacing,
  hasDroneString,
  droneStringStartingFret,
  isLeftHanded,
  fretboardColor,
}) => {
  return (
    <group
      position={[(isLeftHanded ? width - stringSpacing : 0), 0, -0.1]}
      rotation={[0, (isLeftHanded ? degToRad(180) : 0), 0]}
    >
      {hasDroneString ? (
        <>
          {numberOfFrets >= droneStringStartingFret ? (
            <>
              <mesh position={[(width) / 2, -height / 2, -0.05]}>
                <boxGeometry args={[width - stringSpacing, height, 0.1]} />
                <meshBasicMaterial color={fretboardColor} />
              </mesh>
              <mesh
                position={[
                  0,
                  -droneStringStartingFret * fretSpacing -
                  (height - droneStringStartingFret * fretSpacing) / 2,
                  -0.05
                ]}
              >
                <boxGeometry
                  args={[
                    stringSpacing,
                    height - droneStringStartingFret * fretSpacing,
                    0.1
                  ]}
                />
                <meshBasicMaterial color={fretboardColor} />
              </mesh>
            </>
          ) : (
            <mesh
              position={[
                (width) / 2,
                -height / 2,
                -0.05
              ]}
            >
              <boxGeometry args={[width - stringSpacing, height, 0.1]} />
              <meshBasicMaterial color={fretboardColor} />
            </mesh>
          )}
        </>
      ) : (
        <mesh position={[(width - stringSpacing) / 2, -height / 2, -0.05]}>
          <boxGeometry args={[width, height, 0.1]} />
          <meshBasicMaterial color={fretboardColor} />
        </mesh>
      )}
    </group>
  );
};

// Interface for FretVisFlatProps
interface FretVisFlatProps {
  fretIndex: number;
  stringSpacing: number;
  fretSpacing: number;
  tuningLength: number;
  hasDroneString: boolean;
  droneStringStartingFret: number;
  isLeftHanded: boolean;
  fretColor: Color;
}

// Component to visualize a single fret
const FretVisFlat: React.FC<FretVisFlatProps> = ({ fretIndex, stringSpacing, fretSpacing, tuningLength, hasDroneString, droneStringStartingFret, isLeftHanded, fretColor }) => {
  const positionX = hasDroneString
    ? fretIndex >= droneStringStartingFret
      ? ((tuningLength - 1) * stringSpacing) / 2
      : ((tuningLength - 1) * stringSpacing) / 2 + stringSpacing / 2 - (isLeftHanded ? stringSpacing : 0)
    : ((tuningLength - 1) * stringSpacing) / 2;

  const width = hasDroneString && fretIndex < droneStringStartingFret
    ? (tuningLength - 1) * stringSpacing
    : tuningLength * stringSpacing;

  return (
    <mesh position={[positionX, -fretIndex * fretSpacing, 0]}>
      <boxGeometry args={[width, 0.04, 0.05]} />
      <meshBasicMaterial color={fretColor} />
    </mesh>
  );
};

// Interface for StringVisFlatProps
interface StringVisFlatProps {
  fretSpacing: number;
  numberOfFrets: number;
  isDroneString: boolean;
  positionY: number;
  droneStringStartingFret: number;
  stringColor: Color;
}

// Component to visualize a single string
const StringVisFlat: React.FC<StringVisFlatProps> = ({ fretSpacing, numberOfFrets, isDroneString, positionY, droneStringStartingFret, stringColor }) => (
  <>
    {isDroneString && numberOfFrets < droneStringStartingFret ? null : (
      <mesh position={[0, positionY, 0]}>
        <boxGeometry args={[0.1, isDroneString ? (numberOfFrets - droneStringStartingFret) * fretSpacing : numberOfFrets * fretSpacing, 0.1]} />
        <meshBasicMaterial color={stringColor} />
      </mesh>
    )}
  </>
);

// Interface for FretNumberProps
interface FretNumberProps {
  fretIndex: number;
  stringSpacing: number;
  fretSpacing: number;
  tuningLength: number;
  isLeftHanded: boolean;
  fretNumberColor: Color;
  rotation: number;
}

// Component for fret numbers
const FretNumber: React.FC<FretNumberProps> = ({
  fretIndex,
  stringSpacing,
  fretSpacing,
  tuningLength,
  isLeftHanded,
  fretNumberColor,
  rotation
}) => {
  const xPosition = !isLeftHanded
    ? (tuningLength - 0.5) * stringSpacing + 0.27
    : -1.02;
  const yPosition = -fretIndex * fretSpacing + fretSpacing / 2;
  
  return (
    <Text
      position={[xPosition, yPosition, 0.1]}
      fontSize={0.30}
      color={`#${fretNumberColor.getHexString()}`}
      anchorX={(rotation == 0) ? (!isLeftHanded ? "left" : "right") : "center"}
      anchorY="middle"
      rotation={[0, 0, -rotation]}
    >
      {fretIndex}
    </Text>
  );
};

// Interface for CapoProps
interface CapoProps {
  fretIndex: number;
  stringSpacing: number;
  fretSpacing: number;
  tuningLength: number;
  hasDroneString: boolean;
  isLeftHanded: boolean;
  isDroneCapo?: boolean;
  capoColor: Color;
}

// Component to render the capo
const Capo: React.FC<CapoProps> = ({
  fretIndex,
  stringSpacing,
  fretSpacing,
  tuningLength,
  hasDroneString,
  isLeftHanded,
  isDroneCapo = false,
  capoColor
}) => {
  let width, positionX;

  if (isDroneCapo) {
    width = stringSpacing;
    positionX = isLeftHanded ? (tuningLength - 1) * stringSpacing : 0;
  } else {
    if (hasDroneString) {
      width = (tuningLength - 1) * stringSpacing + stringSpacing;
      positionX = isLeftHanded ?
        stringSpacing * tuningLength / 2 - stringSpacing :
        stringSpacing * tuningLength / 2;
    }
    else {
      width = (tuningLength * stringSpacing) + stringSpacing;
      positionX = stringSpacing * tuningLength / 2 - stringSpacing/2;
    }
  }

  return (
    <mesh position={[positionX, -fretIndex * fretSpacing, 0.1]}>
      <boxGeometry args={[width, 0.4, 0.2]} />
      <meshBasicMaterial color={capoColor} />
    </mesh>
  );
};

export default FretboardScene;